/**
 * useUnifiedInstructions — fetches BOTH jphph_change_orders and jphph_instructions
 * for a project, merges them with change_orders taking priority.
 *
 * Returns unified items with approve/decline mutation helpers.
 */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { createNotification } from '@/lib/notifications';
import { isClientChangeOrderRecordVisible, isChangeOrderRowArchived } from '@/lib/changeOrderUtils';

const HIDDEN_INSTRUCTION_STATUSES = new Set(['closed', 'archived', 'deleted', 'cancelled']);

export interface UnifiedInstructionItem {
  id: string;
  source_table: 'change_orders' | 'instructions';
  title: string;
  body: string | null;
  // client_description is the client-visible description (hides internal description)
  client_description: string | null;
  type: string | null;
  status: string;
  requires_client_approval: boolean;
  client_response: string | null;
  client_responded_at: string | null;
  cost_impact: string | null;
  // client_price is the client-facing price (builder_cost is intentionally excluded)
  client_price: number | null;
  schedule_impact: string | null;
  reason: string | null;
  instruction_id: string | null;
  change_order_id: string | null;
  co_number: string | null;
  current_finish_description: string | null;
  new_finish_description: string | null;
  created_at: string;
  priority: string | null;
}

export interface UseUnifiedInstructionsResult {
  items: UnifiedInstructionItem[];
  loading: boolean;
  refresh: () => Promise<void>;
  submitResponse: (id: string, action: 'approved' | 'declined', comment: string) => Promise<boolean>;
  pendingCount: number;
}

function isPendingStatus(status: string): boolean {
  const s = status.toLowerCase();
  return s === 'pending' || s === 'sent' || s === 'sent_to_client' || s === 'pending_client_approval' || s === 'in_progress';
}

function mapChangeOrderStatus(clientAction: 'approved' | 'declined'): string {
  return clientAction === 'approved' ? 'client_approved' : 'client_rejected';
}

function mapInstructionStatus(clientAction: 'approved' | 'declined'): string {
  return clientAction === 'approved' ? 'approved' : 'declined';
}

export function useUnifiedInstructions(projectId: string | null): UseUnifiedInstructionsResult {
  const [items, setItems] = useState<UnifiedInstructionItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!projectId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    // Fetch change_orders (actual management source)
    // NOTE: builder_cost and internal_note are intentionally NOT selected here to keep them off the client
    const { data: coData, error: coErr } = await supabase
      .from('jphph_change_orders')
      .select('id, project_id, title, description, client_description, reason, status, requires_client_approval, client_decision, client_comment, client_decided_at, cost_impact, schedule_impact, instruction_id, co_number, co_sequence, client_price, current_finish_description, new_finish_description, client_visible, created_at')
      .eq('project_id', projectId)
      .order('co_sequence', { ascending: true, nullsFirst: false });

    if (coErr) console.error('Change orders fetch error:', coErr);

    // Fetch instructions (legacy + general)
    const { data: instrData, error: instrErr } = await supabase
      .from('jphph_instructions')
      .select('id, project_id, instruction_title, body, instruction_type, status, requires_client_approval, client_response, client_responded_at, is_shared_with_client, created_at, priority')
      .eq('project_id', projectId)
      .eq('is_shared_with_client', true)
      .order('created_at', { ascending: false });

    if (instrErr) console.error('Instructions fetch error:', instrErr);

    const changeOrders = (coData ?? []) as Record<string, unknown>[];
    const activeChangeOrders = changeOrders.filter((co) =>
      !isChangeOrderRowArchived({ status: String(co.status || '') })
    );
    const visibleChangeOrders = activeChangeOrders.filter((co) =>
      isClientChangeOrderRecordVisible({
        status: String(co.status || ''),
        client_visible: co.client_visible as boolean | null,
        requires_client_approval: co.requires_client_approval as boolean | null,
      })
    );
    const uniqueChangeOrders = Array.from(new Map(visibleChangeOrders.map((co) => [co.id, co])).values());
    const instructions = ((instrData ?? []) as Record<string, unknown>[]).filter(
      (inst) => !HIDDEN_INSTRUCTION_STATUSES.has(String(inst.status || '').toLowerCase())
    );

    const unified: UnifiedInstructionItem[] = [];

    // Add change_orders first
    uniqueChangeOrders.forEach((co) => {
      unified.push({
        id: co.id as string,
        source_table: 'change_orders',
        title: co.title as string,
        // Prefer client_description for client view; fall back to description only if no client_description
        body: (co.client_description as string | null) || null,
        client_description: co.client_description as string | null,
        type: 'change_order',
        status: co.status as string,
        requires_client_approval: (co.requires_client_approval as boolean | null) ?? false,
        client_response: co.client_comment as string | null,
        client_responded_at: co.client_decided_at as string | null,
        cost_impact: co.cost_impact as string | null,
        // Only expose client_price — never expose builder_cost
        client_price: co.client_price as number | null,
        schedule_impact: co.schedule_impact as string | null,
        reason: co.reason as string | null,
        instruction_id: co.instruction_id as string | null,
        change_order_id: co.id as string,
        co_number: co.co_number as string | null,
        current_finish_description: co.current_finish_description as string | null,
        new_finish_description: co.new_finish_description as string | null,
        created_at: co.created_at as string,
        priority: null,
      });
    });

    // Add instructions not already represented
    instructions.forEach((inst) => {
      const alreadyLinked = unified.some(
        (u) => u.instruction_id === inst.id || ((inst.instruction_type as string | null) === 'change_order' && u.change_order_id)
      );
      if (alreadyLinked) return;

      unified.push({
        id: inst.id as string,
        source_table: 'instructions',
        title: inst.instruction_title as string,
        body: inst.body as string | null,
        client_description: null,
        type: (inst.instruction_type as string | null) || 'instruction',
        status: inst.status as string,
        requires_client_approval: (inst.requires_client_approval as boolean | null) ?? false,
        client_response: inst.client_response as string | null,
        client_responded_at: inst.client_responded_at as string | null,
        cost_impact: null,
        client_price: null,
        schedule_impact: null,
        reason: null,
        instruction_id: inst.id as string,
        change_order_id: null,
        co_number: null,
        current_finish_description: null,
        new_finish_description: null,
        created_at: inst.created_at as string,
        priority: inst.priority as string | null,
      });
    });

    unified.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

    if (import.meta.env.DEV) {
      console.log('[useUnifiedInstructions] loaded:', {
        projectId,
        changeOrders: changeOrders.length,
        instructions: instructions.length,
        unified: unified.length,
      });
    }

    setItems(unified);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const submitResponse = useCallback(
    async (id: string, action: 'approved' | 'declined', comment: string): Promise<boolean> => {
      if (!projectId) return false;
      const item = items.find((i) => i.id === id);
      if (!item) return false;

      const now = new Date().toISOString();

      if (item.source_table === 'change_orders') {
        const { error: coErr } = await supabase
          .from('jphph_change_orders')
          .update({
            status: mapChangeOrderStatus(action),
            client_decision: action,
            client_comment: comment.trim() || null,
            client_decided_at: now,
            updated_at: now,
          })
          .eq('id', id);

        if (coErr) {
          console.error('Failed to update change_order:', coErr);
          return false;
        }

        if (item.instruction_id) {
          await supabase
            .from('jphph_instructions')
            .update({
              status: mapInstructionStatus(action),
              client_response: comment.trim() || null,
              client_responded_at: now,
              updated_at: now,
            })
            .eq('id', item.instruction_id);
        }
      } else {
        const { error } = await supabase
          .from('jphph_instructions')
          .update({
            status: mapInstructionStatus(action),
            client_response: comment.trim() || null,
            client_responded_at: now,
            updated_at: now,
          })
          .eq('id', id);

        if (error) {
          console.error('Failed to update instruction:', error);
          return false;
        }
      }

      // Notification to management
      const title = action === 'approved'
        ? `Client approved: ${item.title}`
        : `Client declined: ${item.title}`;

      await createNotification({
        project_id: projectId,
        notification_type: 'instruction_reply',
        title,
        body: comment.trim() || `Client ${action} the ${item.type || 'instruction'}`,
        target_role: 'management',
        link_path: `/management/projects/${projectId}?tab=Instructions`,
        target_type: item.source_table,
        target_id: id,
        related_table: item.source_table === 'change_orders' ? 'jphph_change_orders' : 'jphph_instructions',
        related_id: id,
      });

      await fetchAll();
      return true;
    },
    [items, projectId, fetchAll]
  );

  const pendingCount = items.filter(
    (i) => i.requires_client_approval && isPendingStatus(i.status)
  ).length;

  return {
    items,
    loading,
    refresh: fetchAll,
    submitResponse,
    pendingCount,
  };
}
